// src/components/Admin/AdminAddOrganizationForm.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Checkbox,
  FormControlLabel,
  Divider,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import api from "../../services/api/axios";
import { adminDropdownSx, adminDropdownMenuProps } from "./adminDropdownStyles";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import {
  ACCESS_GROUPS,
  DEFAULT_COMPONENT_CONFIG,
  normalizeComponentConfig,
} from "../../config/accessConfig";

// Optional: Pretty state names
const STATE_NAMES = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
};

// ✅ ALWAYS VISIBLE
const ALWAYS_VISIBLE_LABELS = [
  "Home",
  "Metrics",
  "Research",
];

const moduleCheckboxSx = {
  color: BRAND_GREEN,
  "&.Mui-checked": {
    color: BRAND_GREEN,
  },
  "&.Mui-disabled": {
    color: BRAND_GREEN,
  },
  "&.Mui-checked.Mui-disabled": {
    color: BRAND_GREEN,
  },
};

const accessPanelSx = {
  border: `1px solid ${alpha(BRAND_BLUE, 0.16)}`,
  borderRadius: 2,
  overflow: "hidden",
  backgroundColor: "#fff",
};

const accessGroupHeaderSx = {
  px: 2,
  py: 1.25,
  display: "flex",
  alignItems: { xs: "flex-start", sm: "center" },
  justifyContent: "space-between",
  gap: 1,
  flexDirection: { xs: "column", sm: "row" },
  background: `linear-gradient(180deg, ${alpha(BRAND_BLUE, 0.06)} 0%, ${alpha(
    BRAND_GREEN,
    0.05,
  )} 100%)`,
  borderBottom: `1px solid ${alpha(BRAND_BLUE, 0.12)}`,
};

function ComponentAccessPicker({
  enabledComponents,
  disabled = false,
  onToggleComponent,
  onToggleGroup,
}) {
  const modules = Object.keys(enabledComponents || {});

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: alpha(BRAND_GREEN, 0.08),
          border: `1px solid ${alpha(BRAND_GREEN, 0.18)}`,
        }}
      >
        {ALWAYS_VISIBLE_LABELS.map((label) => (
          <Chip
            key={label}
            label={`${label} always on`}
            size="small"
            sx={{
              color: BRAND_BLUE,
              backgroundColor: "#fff",
              border: `1px solid ${alpha(BRAND_BLUE, 0.18)}`,
              fontWeight: 700,
            }}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        {ACCESS_GROUPS.map((group) => {
          const groupComponents = enabledComponents?.[group.key] || [];
          const selectedCount = group.components.filter((component) =>
            groupComponents.includes(component.key),
          ).length;

          return (
            <Box key={group.key} sx={accessPanelSx}>
              <Box sx={accessGroupHeaderSx}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 900, color: BRAND_BLUE }}
                  >
                    {group.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {selectedCount} of {group.components.length} enabled
                  </Typography>
                </Box>

                <FormControlLabel
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      color: BRAND_BLUE,
                    },
                  }}
                  control={
                    <Checkbox icon={<RadioButtonUncheckedIcon />} checkedIcon={<CheckCircleIcon />} indeterminateIcon={<RemoveCircleIcon />}
                      checked={modules.includes(group.key)}
                      onChange={() => onToggleGroup(group.key)}
                      disabled={disabled}
                      sx={moduleCheckboxSx}
                    />
                  }
                  label="Enabled"
                />
              </Box>

              <Stack spacing={0.75} sx={{ p: 1.25 }}>
                {group.components.map((component) => {
                  const checked = groupComponents.includes(component.key);

                  return (
                    <Box
                      key={`${group.key}.${component.key}`}
                      onClick={() => {
                        if (!disabled) onToggleComponent(group.key, component.key);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        p: 1,
                        borderRadius: 1.5,
                        cursor: disabled ? "default" : "pointer",
                        border: `1px solid ${
                          checked
                            ? alpha(BRAND_GREEN, 0.35)
                            : alpha(BRAND_BLUE, 0.08)
                        }`,
                        backgroundColor: checked
                          ? alpha(BRAND_GREEN, 0.08)
                          : alpha(BRAND_BLUE, 0.02),
                        transition:
                          "background-color 0.15s ease, border-color 0.15s ease",
                        "&:hover": disabled
                          ? {}
                          : {
                              backgroundColor: checked
                                ? alpha(BRAND_GREEN, 0.12)
                                : alpha(BRAND_BLUE, 0.05),
                            },
                      }}
                    >
                      <Checkbox icon={<RadioButtonUncheckedIcon />} checkedIcon={<CheckCircleIcon />} indeterminateIcon={<RemoveCircleIcon />}
                        checked={checked}
                        onChange={() => onToggleComponent(group.key, component.key)}
                        disabled={disabled}
                        onClick={(event) => event.stopPropagation()}
                        sx={{ ...moduleCheckboxSx, p: 0.25 }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 800, color: BRAND_BLUE }}
                        >
                          {component.label}
                        </Typography>
                        {component.defaultEnabled === false && (
                          <Chip
                            label="Default off"
                            size="small"
                            sx={{
                              height: 18,
                              mt: 0.25,
                              mb: 0.25,
                              color: BRAND_BLUE,
                              backgroundColor: alpha(BRAND_BLUE, 0.08),
                              fontWeight: 800,
                              fontSize: "0.68rem",
                            }}
                          />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            lineHeight: 1.35,
                          }}
                        >
                          {component.description}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}

function getToken() {
  try {
    return localStorage.getItem("access_token") || "";
  } catch {
    return "";
  }
}

export default function AdminAddOrganizationForm() {
  const token = getToken();
  const [activeTab, setActiveTab] = useState("create");

  // ---- Create Org state ----
  const [orgName, setOrgName] = useState("");
  const [orgStateAbbr, setOrgStateAbbr] = useState("");
  const [orgGuid, setOrgGuid] = useState("");
  const [orgMonthlyAiCreditLimit, setOrgMonthlyAiCreditLimit] = useState("");
  const [orgBusy, setOrgBusy] = useState(false);
  const [orgErr, setOrgErr] = useState("");
  const [orgSuccess, setOrgSuccess] = useState("");

  const [enabledComponents, setEnabledComponents] = useState(
    DEFAULT_COMPONENT_CONFIG,
  );

  // States only (counties removed)
  const [states, setStates] = useState([]);

  // ---- Users state ----
  const [orgs, setOrgs] = useState([]);
  const [userBusy, setUserBusy] = useState(false);
  const [userErr, setUserErr] = useState("");
  const [userSuccess, setUserSuccess] = useState("");
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    password: "",
    confirm_password: "",
    organization_id: "",
    is_org_admin: false,
  });

  // ---- Delete org state ----
  const [delOrgId, setDelOrgId] = useState("");
  const [delConfirmText, setDelConfirmText] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState("");
  const [delSuccess, setDelSuccess] = useState("");

  // ---- Edit org access state ----
  const [editOrgId, setEditOrgId] = useState("");
  const [editEnabledComponents, setEditEnabledComponents] = useState({});
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState("");
  const [editSuccess, setEditSuccess] = useState("");

  // ---------------- Effects ----------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) return;
      try {
        const [orgRes, statesRes] = await Promise.all([
          api.get("/admin/organizations", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          api.get("/reference/states", {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (cancelled) return;
        setOrgs(orgRes.data || []);
        setStates(statesRes.data || []);
      } catch (e) {
        if (cancelled) return;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // ---------------- Handlers ----------------

  const toggleComponent = (module, component) => {
    setEnabledComponents((prev) => ({
      ...prev,
      [module]: prev[module]?.includes(component)
        ? prev[module].filter((c) => c !== component)
        : [...(prev[module] || []), component],
    }));
  };

  const toggleEditComponent = (module, component) => {
    setEditEnabledComponents((prev) => ({
      ...prev,
      [module]: prev[module]?.includes(component)
        ? prev[module].filter((c) => c !== component)
        : [...(prev[module] || []), component],
    }));
  };

  const toggleComponentGroup = (module) => {
    setEnabledComponents((prev) => {
      if (Object.keys(prev || {}).includes(module)) {
        const updated = { ...prev };
        delete updated[module];
        return updated;
      }
      return {
        ...prev,
        [module]: prev[module] || [],
      };
    });
  };

  const toggleEditComponentGroup = (module) => {
    setEditEnabledComponents((prev) => {
      if (Object.keys(prev || {}).includes(module)) {
        const updated = { ...prev };
        delete updated[module];
        return updated;
      }
      return {
        ...prev,
        [module]: prev[module] || [],
      };
    });
  };

  const accessForOrg = (org) => {
    if (org?.enabled_components && Object.keys(org.enabled_components).length > 0) {
      return normalizeComponentConfig(org.enabled_components);
    }
    return normalizeComponentConfig(org?.effective_components || {});
  };

  const createOrg = async () => {
    setOrgErr("");
    setOrgSuccess("");
    setOrgBusy(true);
    try {
      if (!token) throw new Error("Not authenticated.");

      const payload = {
        name: orgName.trim(),
        state: orgStateAbbr || null,
        guid: orgGuid || null,
        monthly_ai_credit_limit:
          orgMonthlyAiCreditLimit === ""
            ? null
            : Number(orgMonthlyAiCreditLimit),
        enabled_components: normalizeComponentConfig(enabledComponents),
      };

      const res = await api.post("/admin/organizations", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setOrgSuccess(`Created "${res.data.name}" (ID: ${res.data.id})`);

      setOrgName("");
      setOrgGuid("");
      setOrgStateAbbr("");
      setOrgMonthlyAiCreditLimit("");
      setOrgs((prev) =>
        [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)),
      );

      setEnabledComponents(DEFAULT_COMPONENT_CONFIG);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to create organization.";
      setOrgErr(msg);
    } finally {
      setOrgBusy(false);
    }
  };

  const saveOrgAccess = async () => {
    setEditErr("");
    setEditSuccess("");
    setEditBusy(true);
    try {
      if (!token) throw new Error("Not authenticated.");
      const orgIdNum = Number(editOrgId);
      if (!orgIdNum) throw new Error("No organization selected.");

      const payload = {
        enabled_components: normalizeComponentConfig(editEnabledComponents),
      };

      const res = await api.patch(`/admin/organizations/${orgIdNum}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setOrgs((prev) =>
        prev
          .map((org) => (org.id === orgIdNum ? res.data : org))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
      setEditSuccess(`Updated access for "${res.data.name}"`);
    } catch (err) {
      setEditErr(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to update organization access.",
      );
    } finally {
      setEditBusy(false);
    }
  };

  const createUser = async () => {
    setUserErr("");
    setUserSuccess("");

    if (userForm.password !== userForm.confirm_password) {
      setUserErr("Passwords do not match.");
      return;
    }

    setUserBusy(true);
    try {
      const payload = {
        name: userForm.name.trim() || null,
        email: userForm.email,
        password: userForm.password,
        organization_id: Number(userForm.organization_id),
        is_org_admin: Boolean(userForm.is_org_admin),
      };

      const res = await api.post("/admin/users", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUserSuccess(`Created user ${res.data.email}`);
      setUserForm({
        name: "",
        email: "",
        password: "",
        confirm_password: "",
        organization_id: "",
        is_org_admin: false,
      });
    } catch (err) {
      setUserErr(
        err?.response?.data?.detail || err?.message || "Failed to create user.",
      );
    } finally {
      setUserBusy(false);
    }
  };

  const deleteOrg = async () => {
    setDelErr("");
    setDelSuccess("");
    setDelBusy(true);
    try {
      if (!token) throw new Error("Not authenticated.");
      const orgIdNum = Number(delOrgId);
      if (!orgIdNum) throw new Error("No organization selected.");

      await api.delete(`/admin/organizations/${orgIdNum}`, {
        params: { hard: true },
        headers: { Authorization: `Bearer ${token}` },
      });

      const deletedName =
        orgs.find((o) => o.id === orgIdNum)?.name || `ID ${orgIdNum}`;
      setDelSuccess(`Deleted organization "${deletedName}" and its users.`);

      setOrgs((prev) => prev.filter((o) => o.id !== orgIdNum));
      if (String(editOrgId) === String(orgIdNum)) {
        setEditOrgId("");
        setEditEnabledComponents({});
        setEditErr("");
        setEditSuccess("");
      }
      setDelOrgId("");
      setDelConfirmText("");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to delete organization.";
      setDelErr(msg);
    } finally {
      setDelBusy(false);
    }
  };

  // ---------------- Computed ----------------
  const canSubmitOrg = orgName.trim().length > 1;

  const passwordsMatch =
    userForm.password && userForm.password === userForm.confirm_password;

  const canSubmitUser =
    userForm.email &&
    userForm.password?.length >= 8 &&
    passwordsMatch &&
    String(userForm.organization_id || "").length > 0;

  const selectedEnabledModules = Object.keys(enabledComponents || {});

  // ---------------- Render ----------------
  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 0.75,
          borderRadius: 4,
          border: `1px solid ${alpha(BRAND_BLUE, 0.14)}`,
          backgroundColor: alpha(BRAND_BLUE, 0.03),
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, nextTab) => setActiveTab(nextTab)}
          variant="fullWidth"
          sx={{
            minHeight: 52,
            "& .MuiTabs-indicator": {
              height: "100%",
              borderRadius: 3,
              backgroundColor: alpha(BRAND_GREEN, 0.18),
            },
            "& .MuiTab-root": {
              zIndex: 1,
              minHeight: 52,
              textTransform: "none",
              fontWeight: 800,
              color: BRAND_BLUE,
            },
            "& .Mui-selected": {
              color: BRAND_BLUE,
            },
          }}
        >
          <Tab value="create" label="Create Org" />
          <Tab value="edit" label="Edit Org Access" />
          <Tab value="delete" label="Delete Org" />
        </Tabs>
      </Paper>

      {activeTab === "create" && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" },
            gap: 3,
          }}
        >
          <Paper
            elevation={4}
            sx={{ p: 3, border: `2px solid ${BRAND_BLUE}`, borderRadius: 4 }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: BRAND_BLUE }}>
              Create Organization
            </Typography>

            <TextField
              label="Organization Name"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              fullWidth
              margin="normal"
            />

            <TextField
              label="Organization GUID (optional)"
              value={orgGuid}
              onChange={(e) => setOrgGuid(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="123e4567-e89b-12d3-a456-426614174000"
            />

            <TextField
              label="Monthly AI Credit Limit (optional)"
              type="number"
              value={orgMonthlyAiCreditLimit}
              onChange={(e) => setOrgMonthlyAiCreditLimit(e.target.value)}
              fullWidth
              margin="normal"
              inputProps={{ min: 0 }}
              helperText="Leave blank for no monthly cap. Each AI summary request uses 1 credit."
            />

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="state-label">State (abbr)</InputLabel>
              <Select
                sx={adminDropdownSx}
                MenuProps={adminDropdownMenuProps}
                labelId="state-label"
                value={orgStateAbbr}
                label="State (abbr)"
                onChange={(e) => setOrgStateAbbr(String(e.target.value))}
              >
                {states.map((abbr) => (
                  <MenuItem key={abbr} value={abbr}>
                    {abbr} — {STATE_NAMES[abbr]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: BRAND_BLUE }}
            >
              Component Access
            </Typography>

            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
              Core items are always visible. Select the Email, BR&E,
              Prospecting, and Other components this organization can use.
            </Typography>

            <ComponentAccessPicker
              enabledComponents={enabledComponents}
              onToggleComponent={toggleComponent}
              onToggleGroup={toggleComponentGroup}
            />

            <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
              Saved as <b>enabled_components</b>: [
              {selectedEnabledModules.join(", ")}]
            </Typography>

            {orgErr && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {orgErr}
              </Alert>
            )}
            {orgSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {orgSuccess}
              </Alert>
            )}

            <Button
              variant="contained"
              onClick={createOrg}
              disabled={!canSubmitOrg || orgBusy || !token}
              sx={{ mt: 2 }}
              fullWidth
            >
              {orgBusy ? "Creating..." : "Create Organization"}
            </Button>
          </Paper>

          <Paper
            elevation={4}
            sx={{ p: 3, border: `2px solid ${BRAND_BLUE}`, borderRadius: 4 }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: BRAND_BLUE }}>
              Create User
            </Typography>

            <FormControl fullWidth margin="normal">
              <InputLabel id="org-select">Organization</InputLabel>
              <Select
                sx={adminDropdownSx}
                MenuProps={adminDropdownMenuProps}
                labelId="org-select"
                label="Organization"
                value={userForm.organization_id}
                onChange={(e) =>
                  setUserForm({ ...userForm, organization_id: e.target.value })
                }
              >
                {orgs.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Name (optional)"
              value={userForm.name}
              onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              fullWidth
              margin="normal"
            />

            <TextField
              label="Email"
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              fullWidth
              margin="normal"
            />

            <TextField
              label="Password"
              type="password"
              value={userForm.password}
              onChange={(e) =>
                setUserForm({ ...userForm, password: e.target.value })
              }
              fullWidth
              margin="normal"
              helperText="Minimum 8 characters"
            />

            <TextField
              label="Confirm Password"
              type="password"
              value={userForm.confirm_password}
              onChange={(e) =>
                setUserForm({ ...userForm, confirm_password: e.target.value })
              }
              fullWidth
              margin="normal"
              error={
                userForm.confirm_password.length > 0 &&
                userForm.confirm_password !== userForm.password
              }
              helperText={
                userForm.confirm_password.length > 0 &&
                userForm.confirm_password !== userForm.password
                  ? "Passwords do not match"
                  : ""
              }
            />

            <FormControlLabel
              control={
                <Checkbox icon={<RadioButtonUncheckedIcon />} checkedIcon={<CheckCircleIcon />} indeterminateIcon={<RemoveCircleIcon />}
                  checked={userForm.is_org_admin}
                  onChange={(e) =>
                    setUserForm({ ...userForm, is_org_admin: e.target.checked })
                  }
                />
              }
              label="Make this user an organization admin"
            />

            {userErr && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {userErr}
              </Alert>
            )}
            {userSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {userSuccess}
              </Alert>
            )}

            <Button
              variant="contained"
              onClick={createUser}
              disabled={!canSubmitUser || userBusy || !token}
              sx={{ mt: 2 }}
              fullWidth
            >
              {userBusy ? "Creating..." : "Create User"}
            </Button>
          </Paper>
        </Box>
      )}

      {activeTab === "edit" && (
        <Paper
          elevation={4}
          sx={{
            p: 3,
            border: `2px solid ${BRAND_GREEN}`,
            borderRadius: 4,
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ color: BRAND_BLUE }}>
            Edit Organization Access
          </Typography>

          <FormControl fullWidth margin="normal">
            <InputLabel id="edit-org-select">Organization</InputLabel>
            <Select
              sx={adminDropdownSx}
              MenuProps={adminDropdownMenuProps}
              labelId="edit-org-select"
              label="Organization"
              value={editOrgId}
              onChange={(e) => {
                const nextId = String(e.target.value);
                const org = orgs.find((item) => String(item.id) === nextId);
                setEditOrgId(nextId);
                setEditEnabledComponents(accessForOrg(org));
                setEditErr("");
                setEditSuccess("");
              }}
            >
              {orgs.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            Older organizations with no component config use their legacy BR&E
            or Prospecting module access until you save explicit components
            here.
          </Typography>

          <ComponentAccessPicker
            enabledComponents={editEnabledComponents}
            disabled={!editOrgId}
            onToggleComponent={toggleEditComponent}
            onToggleGroup={toggleEditComponentGroup}
          />

          {editErr && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {editErr}
            </Alert>
          )}
          {editSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {editSuccess}
            </Alert>
          )}

          <Button
            variant="contained"
            onClick={saveOrgAccess}
            disabled={!editOrgId || editBusy || !token}
            sx={{ mt: 2 }}
          >
            {editBusy ? "Saving..." : "Save Access"}
          </Button>
        </Paper>
      )}

      {activeTab === "delete" && (
        <Paper
          elevation={4}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "error.light",
            backgroundColor: "rgba(244, 67, 54, 0.04)",
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Danger Zone — Delete Organization
          </Typography>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "flex-start" }}
          >
            <FormControl sx={{ minWidth: { xs: "100%", md: 220 } }}>
              <InputLabel id="del-org-select">Organization</InputLabel>
              <Select
                sx={adminDropdownSx}
                MenuProps={adminDropdownMenuProps}
                labelId="del-org-select"
                label="Organization"
                value={delOrgId}
                onChange={(e) => {
                  setDelOrgId(String(e.target.value));
                  setDelConfirmText("");
                  setDelErr("");
                  setDelSuccess("");
                }}
              >
                {orgs.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={
                delOrgId
                  ? "Type name to confirm deletion"
                  : "Select an organization"
              }
              value={delConfirmText}
              onChange={(e) => setDelConfirmText(e.target.value)}
              fullWidth
              disabled={!delOrgId}
            />

            <Button
              variant="contained"
              color="error"
              onClick={deleteOrg}
              sx={{ whiteSpace: "nowrap", minHeight: 56 }}
              disabled={
                !delOrgId ||
                delConfirmText !==
                  (orgs.find((o) => o.id === Number(delOrgId))?.name || "")
              }
            >
              {delBusy ? "Deleting..." : "Delete Org"}
            </Button>
          </Stack>

          {delErr && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {delErr}
            </Alert>
          )}
          {delSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {delSuccess}
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
}
